import type {
  SearchDocument,
  SearchResultSet,
} from '@backstage/plugin-search-common';
import type { SearchHit } from './searchModel';

// Catalog search documents carry extra fields beyond the base SearchDocument;
// they're typed `unknown` and read defensively so a non-string never crashes.
type CatalogDoc = SearchDocument & {
  kind?: unknown;
  owner?: unknown;
  owners?: unknown;
  lifecycle?: unknown;
};

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v ? v : undefined;

/**
 * All owners of a result: the enriched `owners` array (collator), falling back
 * to the single legacy `owner` field for documents indexed before the collator
 * change. `undefined` when there are none, so the row omits the owner line.
 */
const owners = (doc: CatalogDoc): string[] | undefined => {
  if (Array.isArray(doc.owners)) {
    const list = doc.owners.filter((v): v is string => typeof v === 'string' && !!v);
    if (list.length) return list;
  }
  const single = str(doc.owner);
  return single ? [single] : undefined;
};

/**
 * Map one backend search result to the Backstage-free `SearchHit` view-model.
 * Shared by the `/search` page and the quick-search palette containers (the
 * only places coupled to the search backend's result shape).
 */
export function toSearchHit(
  result: SearchResultSet['results'][number],
): SearchHit {
  const doc = result.document as CatalogDoc;
  return {
    type: result.type,
    title: doc.title,
    text: doc.text,
    location: doc.location,
    kind: str(doc.kind),
    owners: owners(doc),
    lifecycle: str(doc.lifecycle),
  };
}
